import { Session } from '../models/session.model';

/**
 * Deterministic — no Date.now(), no randomness (mock-data seed convention). Groups are
 * hand-built rather than run through `runSplit`: they only need to partition each row's
 * `participantIds` consistently, not reproduce the exact algorithm output.
 *
 * All ids are real, verified against `invitations.seed.ts`:
 *  - Invitation ids/names/rosterIds are copied verbatim.
 *  - Every `participantIds` entry is a participant seeded with state 'scored' and either
 *    `unlocked: true` (the default from `part()`) or explicitly unlocked — i.e. eligible per
 *    `InvitationMockService.eligibleParticipants`. Locked/started/not_started ids (e.g. bp8-bp16,
 *    xr8, p2/p3, p9, p14/p15, p18) are deliberately excluded.
 *  - Every group `memberIds` entry appears in that session's own `participantIds`.
 *
 * Expiry: most rows use a far-future `expiresAt` (2027+); two rows (ses-8, ses-9) use a fixed
 * past date so the Expired filter has content; ses-11 OMITS `expiresAt` entirely — expiry is
 * optional, and a row with none never expires, so the state is exercised in the demo data.
 */
export const SESSIONS_SEED: Session[] = [
  {
    id: 'ses-1', name: 'Q3 Leadership Cohort',
    primaryInvitationId: 'inv-company-summit',
    primaryInvitationName: 'Company-Wide Annual Strategy Summit & Team Effectiveness Program',
    sources: [{ kind: 'invitation', id: 'inv-company-summit' }],
    participantIds: ['p-40', 'p-41', 'p-42', 'p-43'],
    splitType: 'diverse_exact',
    groups: [
      { id: 'g-1', name: 'Group 1', memberIds: ['p-40', 'p-42'] },
      { id: 'g-2', name: 'Group 2', memberIds: ['p-41', 'p-43'] },
    ],
    expiresAt: '2027-12-31T17:00:00.000Z',
    createdBy: 'Test Owner', createdAt: '2026-06-18T12:00:00.000Z', updatedAt: '2026-06-18T12:00:00.000Z',
  },
  {
    id: 'ses-2', name: 'Exec Retreat Breakouts',
    primaryInvitationId: 'inv-exec-retreat',
    primaryInvitationName: 'Executive Leadership Offsite Retreat & Team Effectiveness Intensive',
    sources: [{ kind: 'invitation', id: 'inv-exec-retreat' }],
    participantIds: ['p5', 'xr1', 'xr2', 'xr3', 'xr4', 'xr5', 'xr6', 'xr7'],
    splitType: 'similar',
    groups: [
      { id: 'g-1', name: 'Group 1', memberIds: ['p5', 'xr1', 'xr2', 'xr3'] },
      { id: 'g-2', name: 'Group 2', memberIds: ['xr4', 'xr5', 'xr6', 'xr7'] },
    ],
    expiresAt: '2027-06-01T09:00:00.000Z',
    createdBy: 'Test Owner', createdAt: '2026-06-19T10:00:00.000Z', updatedAt: '2026-06-19T10:00:00.000Z',
  },
  {
    id: 'ses-3', name: 'Product Discovery Trios',
    primaryInvitationId: 'inv-product-discovery',
    primaryInvitationName: 'Product Discovery & Customer Insight Alignment Workshop',
    sources: [{ kind: 'invitation', id: 'inv-product-discovery' }],
    participantIds: ['pd1', 'pd2', 'pd3', 'pd4', 'pd5', 'pd6', 'pd7'],
    splitType: 'diverse_exact',
    groups: [
      { id: 'g-1', name: 'Group 1', memberIds: ['pd1', 'pd4', 'pd7'] },
      { id: 'g-2', name: 'Group 2', memberIds: ['pd2', 'pd5'] },
      { id: 'g-3', name: 'Group 3', memberIds: ['pd3', 'pd6'] },
    ],
    expiresAt: '2027-05-20T14:00:00.000Z',
    createdBy: 'Test Owner', createdAt: '2026-06-20T11:00:00.000Z', updatedAt: '2026-06-20T11:00:00.000Z',
  },
  {
    id: 'ses-4', name: 'Customer Success Cohort Split',
    primaryInvitationId: 'inv-customer-success',
    primaryInvitationName: 'Customer Success Organization Cross-Functional Cohort',
    sources: [{ kind: 'invitation', id: 'inv-customer-success' }],
    participantIds: ['p13', 'cs1', 'cs2', 'cs3', 'cs4', 'cs5', 'cs6', 'cs7'],
    splitType: 'diverse_exact',
    groups: [
      { id: 'g-1', name: 'Group 1', memberIds: ['p13', 'cs1', 'cs3', 'cs5', 'cs7'] },
      { id: 'g-2', name: 'Group 2', memberIds: ['cs2', 'cs4', 'cs6'] },
    ],
    expiresAt: '2027-07-15T16:00:00.000Z',
    createdBy: 'Test Owner', createdAt: '2026-06-21T09:30:00.000Z', updatedAt: '2026-06-21T09:30:00.000Z',
  },
  {
    id: 'ses-5', name: 'Board Prep Working Groups',
    primaryInvitationId: 'inv-board-prep',
    primaryInvitationName: 'Board of Directors Strategic Offsite Preparation Session',
    sources: [{ kind: 'invitation', id: 'inv-board-prep' }],
    participantIds: ['bd1', 'bd2', 'bd3', 'bd4', 'bd5', 'bd6'],
    splitType: 'diverse_exact',
    groups: [
      { id: 'g-1', name: 'Group 1', memberIds: ['bd2', 'bd4', 'bd6'] },
      { id: 'g-2', name: 'Group 2', memberIds: ['bd1', 'bd3', 'bd5'] },
    ],
    expiresAt: '2027-08-01T08:00:00.000Z',
    createdBy: 'Test Owner', createdAt: '2026-06-22T13:00:00.000Z', updatedAt: '2026-06-22T13:00:00.000Z',
  },
  {
    id: 'ses-6', name: 'Cross-Org Leadership Blend',
    primaryInvitationId: 'inv-company-summit',
    primaryInvitationName: 'Company-Wide Annual Strategy Summit & Team Effectiveness Program',
    sources: [
      { kind: 'invitation', id: 'inv-company-summit' },
      { kind: 'invitation', id: 'inv-exec-retreat' },
    ],
    participantIds: ['p-44', 'p-45', 'xr4', 'xr5'],
    splitType: 'diverse_exact',
    groups: [
      { id: 'g-1', name: 'Group 1', memberIds: ['p-44', 'xr4'] },
      { id: 'g-2', name: 'Group 2', memberIds: ['p-45', 'xr5'] },
    ],
    expiresAt: '2027-09-10T10:00:00.000Z',
    createdBy: 'Test Owner', createdAt: '2026-06-23T15:00:00.000Z', updatedAt: '2026-06-23T15:00:00.000Z',
  },
  {
    id: 'ses-7', name: 'Cross-Functional Managers Sample',
    primaryInvitationId: 'inv-hr-development',
    primaryInvitationName: 'Human Resources Talent Development & Succession Planning Program',
    sources: [
      { kind: 'invitation', id: 'inv-hr-development' },
      { kind: 'invitation', id: 'inv-finance-team' },
      { kind: 'invitation', id: 'inv-data-science' },
    ],
    participantIds: ['p11', 'p12', 'p16'],
    splitType: 'diverse_exact',
    groups: [
      { id: 'g-1', name: 'Group 1', memberIds: ['p11', 'p16'] },
      { id: 'g-2', name: 'Group 2', memberIds: ['p12'] },
    ],
    expiresAt: '2027-04-05T12:00:00.000Z',
    createdBy: 'Test Owner', createdAt: '2026-06-24T08:00:00.000Z', updatedAt: '2026-06-24T08:00:00.000Z',
  },
  {
    id: 'ses-8', name: 'Marketing Sprint Pairs',
    primaryInvitationId: 'inv-marketing-sprint',
    primaryInvitationName: 'Marketing Department Quarterly Planning & Creative Sprint',
    sources: [{ kind: 'invitation', id: 'inv-marketing-sprint' }],
    participantIds: ['p7', 'p8'],
    splitType: 'diverse_exact',
    groups: [
      { id: 'g-1', name: 'Group 1', memberIds: ['p7'] },
      { id: 'g-2', name: 'Group 2', memberIds: ['p8'] },
    ],
    expiresAt: '2026-05-01T12:00:00.000Z',
    createdBy: 'Test Owner', createdAt: '2026-04-01T09:00:00.000Z', updatedAt: '2026-04-01T09:00:00.000Z',
  },
  {
    id: 'ses-9', name: 'Sales & Startup Founders Mixer',
    primaryInvitationId: 'inv-sales-kickoff',
    primaryInvitationName: 'Annual Sales Kickoff & Revenue Enablement Conference 2026',
    sources: [
      { kind: 'invitation', id: 'inv-sales-kickoff' },
      { kind: 'invitation', id: 'inv-startup-cohort' },
    ],
    participantIds: ['p4', 'p17'],
    splitType: 'similar',
    groups: [
      { id: 'g-1', name: 'Group 1', memberIds: ['p4'] },
      { id: 'g-2', name: 'Group 2', memberIds: ['p17'] },
    ],
    expiresAt: '2026-04-15T09:00:00.000Z',
    createdBy: 'Test Owner', createdAt: '2026-03-15T09:00:00.000Z', updatedAt: '2026-03-15T09:00:00.000Z',
  },
  {
    id: 'ses-10', name: 'Company Summit Full Cohort Split',
    primaryInvitationId: 'inv-company-summit',
    primaryInvitationName: 'Company-Wide Annual Strategy Summit & Team Effectiveness Program',
    sources: [{ kind: 'invitation', id: 'inv-company-summit' }],
    participantIds: ['bp1', 'bp2', 'bp3', 'bp4', 'bp5', 'bp6', 'bp7', 'p-46', 'p-47', 'p-48', 'p-49', 'p-50'],
    splitType: 'diverse_exact',
    groups: [
      { id: 'g-1', name: 'Group 1', memberIds: ['bp1', 'bp4', 'bp7', 'p-49'] },
      { id: 'g-2', name: 'Group 2', memberIds: ['bp2', 'bp5', 'p-46', 'p-50'] },
      { id: 'g-3', name: 'Group 3', memberIds: ['bp3', 'bp6', 'p-47', 'p-48'] },
    ],
    expiresAt: '2027-10-20T17:00:00.000Z',
    createdBy: 'Test Owner', createdAt: '2026-06-25T14:00:00.000Z', updatedAt: '2026-06-25T14:00:00.000Z',
  },
  {
    id: 'ses-11', name: 'Org-Wide Sample Pull (r2)',
    primaryInvitationId: 'inv-company-summit',
    primaryInvitationName: 'Company-Wide Annual Strategy Summit & Team Effectiveness Program',
    sources: [{ kind: 'org', id: 'r2' }],
    participantIds: ['p-51', 'p-52', 'p16'],
    splitType: 'diverse_exact',
    groups: [
      { id: 'g-1', name: 'Group 1', memberIds: ['p-51', 'p16'] },
      { id: 'g-2', name: 'Group 2', memberIds: ['p-52'] },
    ],
    // No `expiresAt`: this group never expires.
    createdBy: 'Test Owner', createdAt: '2026-06-26T16:00:00.000Z', updatedAt: '2026-06-26T16:00:00.000Z',
  },
];
